import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkle, PaperPlaneRight, ArrowsClockwise, Check, WhatsappLogo, EnvelopeSimple, ChatTeardropText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `/api`;

const FollowupGenerator = ({ leadId, leadName }) => {
  const [tone, setTone] = useState('profissional');
  const [channel, setChannel] = useState('whatsapp');
  const [customContext, setCustomContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (leadId) fetchHistory();
  }, [leadId]);

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/leads/${leadId}/followups`, { withCredentials: true });
      setHistory(data.followups || []);
    } catch (error) {
      console.error('Error fetching followups:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const generateMessage = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/ai/followup/generate`,
        { lead_id: leadId, tone, channel, context: customContext || null },
        { withCredentials: true }
      );
      setMessage(data.message);
      toast.success('Mensagem gerada pela IA!');
    } catch (error) {
      toast.error('Erro ao gerar mensagem');
      console.error('Error:', error);
    } finally {
      setGenerating(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) {
      toast.error('Mensagem vazia');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/followups`,
        { lead_id: leadId, message: message, channel, status: 'sent' },
        { withCredentials: true }
      );
      toast.success(`Follow-up enviado via ${channel === 'whatsapp' ? 'WhatsApp' : channel === 'email' ? 'E-mail' : 'SMS'}!`);
      setMessage('');
      setCustomContext('');
      fetchHistory();
    } catch (error) {
      toast.error('Erro ao enviar follow-up');
      console.error('Error:', error);
    } finally {
      setSending(false);
    }
  };

  const getChannelIcon = (ch) => {
    if (ch === 'whatsapp') return <WhatsappLogo size={16} weight="fill" className="text-emerald-600" />;
    if (ch === 'email') return <EnvelopeSimple size={16} weight="duotone" className="text-blue-600" />;
    return <ChatTeardropText size={16} weight="duotone" className="text-purple-600" />;
  };

  return (
    <div className="space-y-5" data-testid="followup-generator">
      {/* Configuration */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={18} weight="fill" className="text-purple-600" />
          <p className="text-sm font-semibold text-purple-900">
            Configurar mensagem para {leadName}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">Tom da mensagem</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              data-testid="followup-tone-select"
            >
              <option value="profissional">Profissional</option>
              <option value="amigavel">Amigável</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">Canal</label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              data-testid="followup-channel-select"
            >
              <option value="whatsapp">WhatsApp</option>
              <option value="email">E-mail</option>
              <option value="sms">SMS</option>
            </select>
          </div>
        </div>

        <div className="mb-3">
          <label className="text-xs font-medium text-slate-700 mb-1.5 block">
            Contexto adicional (opcional)
          </label>
          <input
            type="text"
            value={customContext}
            onChange={(e) => setCustomContext(e.target.value)}
            placeholder="Ex: Lead pediu desconto na última conversa"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
            data-testid="followup-context-input"
          />
        </div>

        <button
          onClick={generateMessage}
          disabled={generating}
          className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="generate-followup-button"
        >
          {generating ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              IA gerando mensagem...
            </>
          ) : (
            <>
              <Sparkle size={16} weight="fill" />
              Gerar com IA
            </>
          )}
        </button>
      </div>

      {/* Generated Message Editor */}
      {message && (
        <div className="bg-white border border-slate-300 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-900">Mensagem gerada (edite se quiser)</p>
            <button
              onClick={generateMessage}
              className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
              data-testid="regenerate-button"
            >
              <ArrowsClockwise size={14} />
              Regenerar
            </button>
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={channel === 'email' ? 10 : 5}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
            data-testid="followup-message-editor"
          />
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => setMessage('')}
              className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="discard-followup-button"
            >
              Descartar
            </button>
            <button
              onClick={sendMessage}
              disabled={sending}
              className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
              data-testid="send-followup-button"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <PaperPlaneRight size={14} weight="fill" />
                  Aprovar e Enviar
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Check size={16} weight="duotone" className="text-emerald-600" />
          Histórico de Follow-ups
        </h4>
        {loadingHistory ? (
          <p className="text-sm text-slate-500 text-center py-4">Carregando...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4 bg-slate-50 rounded-lg border border-slate-200">
            Nenhum follow-up enviado ainda
          </p>
        ) : (
          <div className="space-y-2">
            {history.map((fup) => (
              <div
                key={fup.id}
                className="p-3 bg-slate-50 border border-slate-200 rounded-lg"
                data-testid={`followup-history-${fup.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getChannelIcon(fup.channel)}
                    <span className="text-xs font-medium text-slate-700 capitalize">{fup.channel}</span>
                    <span className="text-xs text-slate-400">por {fup.sent_by_name}</span>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(fup.created_at).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {fup.message}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowupGenerator;
